import { produce, type WritableDraft } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import { type CanvasElementId } from '@blaze-runtime/engine-domain';
import { resolveElementMetaDisplayValue } from '@blaze-runtime/engine-domain';
import { type ElementMetaData } from '@blaze-shared/elements-domain';
import { setValueByPath } from '@blaze-shared/workflow-domain';

import { isElementType } from '../../canvas-elements/element-model/element-type-guards';

// TODO: test
export function updateElementWidth(
  state: EngineStateBase,
  elementId: CanvasElementId,
  width: number,
): EngineStateBase {
  return produce(state, (draft) => {
    const element = draft.entities[elementId];

    if (!element) {
      throw new Error(`Could not find element id ${elementId}`);
    }

    if (width === 0) {
      element.width = { ...element.width, mode: 'auto' };
    } else if (width === Infinity) {
      element.width = { ...element.width, mode: 'full' };
    } else {
      element.width = { ...element.width, mode: 'value', value: width };
    }
    setValueByPath(element, 'props.dirty.width', true);
  });
}

// TODO: test
export function updateElementHeight(
  state: EngineStateBase,
  elementId: CanvasElementId,
  height: number,
  elementMeta?: ElementMetaData,
): EngineStateBase {
  return produce(state, (draft) =>
    updateElementHeightMutably(draft, elementId, height, elementMeta),
  );
}

export function updateElementHeightMutably(
  draft: WritableDraft<EngineStateBase>,
  elementId: CanvasElementId,
  height: number,
  elementMeta?: ElementMetaData,
): void {
  const element = draft.entities[elementId];

  if (!element) {
    throw new Error(`Could not find element id ${elementId}`);
  }

  // For textInput and textField element height must be fixed while resizing to make overflow behavior work.
  if (isElementType(element, 'textField') || isElementType(element, 'textInput')) {
    const format = elementMeta
      ? resolveElementMetaDisplayValue('content.input.format', 'format', elementMeta, undefined)
      : element.props?.content?.input?.format;

    const isParagraph = format === 'paragraph';
    element.height = { ...element.height, mode: 'value', value: height, fixed: isParagraph };
  }
  // Default behavior
  else {
    if (height === 0) {
      element.height = { ...element.height, mode: 'auto' };
    } else {
      element.height = { ...element.height, mode: 'value', value: height };
    }
  }
  setValueByPath(element, 'props.dirty.height', true);
}
